import * as XLSX from "xlsx";
import type { ImportRowLog } from "@/project/karuna/utils/schoolImportRowEngine";
import { buildImportLogSheetRows } from "@/project/karuna/utils/schoolImportLogFormat";

export interface SchemaFieldOption {
  label: string;
  value: string;
}

export const SCHOOL_EXPORT_FIELDS: SchemaFieldOption[] = [
  { label: "KC NO", value: "kcno" },
  { label: "School Name", value: "schoolname" },
  { label: "Principal's Name", value: "principal" },
  { label: "School Contact Number", value: "contact" },
  { label: "School Email", value: "schoolemail" },
  { label: "Nearest Karuna Kendra", value: "kendra" },
  { label: "Address 1", value: "address1" },
  { label: "Address 2", value: "address2" },
  { label: "City", value: "city" },
  { label: "Pin-code", value: "pincode" },
  { label: "State", value: "state" },
  { label: "Date of Formation", value: "dateofformation" },
  { label: "Contact no./Fax no.", value: "contactfax" },
  { label: "Email ID", value: "emailid" },
  { label: "Mobile no.", value: "mobileno" },
  { label: "Additional Details", value: "additionaldetails" },
  { label: "Teacher Name", value: "teachername" },
  { label: "Teacher Mobile Number", value: "teachercontact" },
  { label: "Teacher Email", value: "teacheremail" },
  { label: "Status", value: "status" },
];

export const SCHOOL_EXCEL_SYSTEM_FIELDS = [
  "id",
  "is_active",
  "created_on",
  "updated_on",
  "created_by",
  "updated_by",
  "tenant",
  "created_by_id",
  "updated_by_id",
  "tenant_id",
];

const FIELD_ALIASES: Record<string, string[]> = {
  kcno: ["kc_no", "kc no", "kc number", "karuna club no", "kcno"],
  schoolname: ["school_name", "school name", "name", "schoolname"],
  principal: ["principal_name", "principal name", "principalname"],
  contact: [
    "contact_number",
    "phone",
    "mobile",
    "school contact",
    "schoolcontact",
    "school_contact",
  ],
  schoolemail: ["school_email", "email", "school email"],
  kendra: ["kendra_name", "nearest kendra", "nearestkendra", "kendra"],
  address1: ["address 1", "address1", "address line 1", "addr1"],
  address2: ["address 2", "address2", "address line 2", "addr2"],
  city: ["city", "town"],
  pincode: ["pin code", "pincode", "pin", "zip", "postal code"],
  state: ["state", "province"],
  dateofformation: [
    "date of formation",
    "dateofformation",
    "formation date",
    "established",
    "founded",
  ],
  contactfax: [
    "contact no",
    "fax no",
    "contact/fax",
    "contactfax",
    "fax",
    "contact no fax no",
  ],
  emailid: ["email id", "emailid", "email address"],
  mobileno: ["mobile no", "mobileno", "mobile number", "mobile"],
  additionaldetails: [
    "additional details",
    "additionaldetails",
    "notes",
    "remarks",
    "details",
  ],
  status: ["approval status", "school status"],
};

const normalizeKey = (value: string) =>
  value.toLowerCase().replace(/[\s_\-./]/g, "");

export function filterMappableSchemaFields(
  grid: Array<{ field_name: string; field_title: string }> | undefined
): SchemaFieldOption[] {
  return (
    grid
      ?.filter(
        field =>
          !SCHOOL_EXCEL_SYSTEM_FIELDS.includes(field.field_name.toLowerCase())
      )
      ?.map(field => ({
        label: field.field_title,
        value: field.field_name.toLowerCase(),
      })) ?? []
  );
}

export function autoMapExcelColumns(
  excelHeaders: string[],
  schemaFields: SchemaFieldOption[]
): Record<string, string> {
  const mapping: Record<string, string> = {};
  const normalizedHeaders = excelHeaders.map(header => ({
    original: header,
    normalized: normalizeKey(header),
  }));

  for (const field of schemaFields) {
    const candidates = new Set<string>([
      normalizeKey(field.value),
      normalizeKey(field.label),
      ...(FIELD_ALIASES[field.value] ?? []).map(normalizeKey),
    ]);

    const match = normalizedHeaders.find(
      header =>
        candidates.has(header.normalized) ||
        [...candidates].some(
          candidate =>
            candidate.length > 2 &&
            (header.normalized.includes(candidate) ||
              candidate.includes(header.normalized))
        )
    );

    if (match) {
      mapping[field.value] = match.original;
    }
  }

  return mapping;
}

export function getSchoolFieldValue(
  record: Record<string, any>,
  fieldName: string
): string | number | boolean {
  const aliases: Record<string, string[]> = {
    kcno: ["kcno", "kc_no"],
    schoolname: ["schoolname", "school_name"],
    principal: ["principal", "principalname", "principal_name"],
    contact: ["contact", "schoolcontact", "school_contact", "contact_number"],
    schoolemail: ["schoolemail", "school_email", "email"],
    kendra: ["kendra", "nearestkendra", "kendra_name"],
    address1: ["address1", "address_1"],
    address2: ["address2", "address_2"],
    city: ["city"],
    pincode: ["pincode", "pin_code"],
    state: ["state"],
    dateofformation: ["dateofformation", "date_of_formation"],
    contactfax: ["contactfax", "contact_fax"],
    emailid: ["emailid", "email_id"],
    mobileno: ["mobileno", "mobile_no"],
    additionaldetails: ["additionaldetails", "additional_details"],
    status: ["status"],
  };

  const keys = [fieldName, ...(aliases[fieldName] ?? [])];
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}

export async function parseExcelFile(file: File): Promise<{
  headers: string[];
  rows: Record<string, unknown>[];
}> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    throw new Error("The Excel file does not contain any sheets.");
  }

  const sheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: "",
  });

  if (rawRows.length === 0) {
    throw new Error("The Excel file is empty.");
  }

  const headers = (rawRows[0] ?? [])
    .map(value => String(value ?? "").trim())
    .filter(Boolean);

  if (headers.length === 0) {
    throw new Error("Could not find column headers in the first row.");
  }

  const rows = rawRows
    .slice(1)
    .map(row => {
      const record: Record<string, unknown> = {};
      headers.forEach((header, index) => {
        record[header] = Array.isArray(row) ? (row[index] ?? "") : "";
      });
      return record;
    })
    .filter(row =>
      Object.values(row).some(
        value => value !== "" && value !== null && value !== undefined
      )
    );

  return { headers, rows };
}

export function exportSchoolsToExcel(
  records: Record<string, any>[],
  fields: SchemaFieldOption[],
  filename: string
) {
  const headerRow = fields.map(field => field.label);
  const dataRows = records.map(record =>
    fields.map(field => getSchoolFieldValue(record, field.value))
  );

  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Schools");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

export function exportSchoolTemplateExcel(
  fields: SchemaFieldOption[],
  filename: string
) {
  exportSchoolsToExcel([], fields, filename);
}

export function exportRowsToExcel(
  rows: Record<string, unknown>[],
  filename: string,
  sheetName = "Sheet1"
) {
  if (rows.length === 0) {
    throw new Error("No rows to export.");
  }

  const headers = Array.from(
    rows.reduce<Set<string>>((set, row) => {
      Object.keys(row).forEach(key => set.add(key));
      return set;
    }, new Set<string>())
  );

  const dataRows = rows.map(row => headers.map(header => row[header] ?? ""));
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

export function exportImportLogSheet(
  logs: ImportRowLog[],
  filename: string,
  sheetName: string
) {
  if (logs.length === 0) {
    throw new Error("No rows to export.");
  }

  const sheetRows = buildImportLogSheetRows(logs);
  const headers: string[] = ["Row Number", "Action", "Status", "Message"];
  const dataRows = sheetRows.map(row => [
    row["Row Number"],
    row.Action,
    row.Status,
    row.Message,
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  worksheet["!cols"] = [{ wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 100 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

export function exportImportResultFiles(
  successLogs: ImportRowLog[],
  failedLogs: ImportRowLog[],
  batchId: string
) {
  const stamp = new Date().toISOString().split("T")[0];
  const shortBatch = batchId.slice(0, 8);

  if (successLogs.length > 0) {
    exportImportLogSheet(
      successLogs,
      `success_rows_${stamp}_${shortBatch}`,
      "Success"
    );
  }

  if (failedLogs.length > 0) {
    exportImportLogSheet(
      failedLogs,
      `failed_rows_${stamp}_${shortBatch}`,
      "Failed"
    );
  }
}

export function mapExcelRowToFormValues(
  row: Record<string, unknown>,
  columnMapping: Record<string, string>
): Record<string, unknown> {
  const formValues: Record<string, unknown> = { status: "approved" };

  for (const [dbField, excelColumn] of Object.entries(columnMapping)) {
    if (!excelColumn) {
      continue;
    }

    const value = row[excelColumn];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      formValues[dbField] = value;
    }
  }

  return formValues;
}
